// @ts-nocheck
/* eslint-disable spire/export-styles */
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import getColor from "@insite/mobius/utilities/getColor";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import Typography, { TypographyProps } from "@store/MobiusOverrides/Typography/Typography";
import breakpoint from "@toolkit/Framework/Common/Utilities/breakpoint";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import ArrowForward from "@toolkit/Library/Components/Icons/ArrowForward";
import { Alignment } from "embla-carousel/embla-carousel-vanilla/alignment";
import parse from "html-react-parser";
import * as React from "react";
import { FC } from "react";
import { css } from "styled-components";

const enum fields {
    size = "size",
    label = "label",
    link = "link",
    description = "description",
    alignment = "alignment",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.size]: "boxed" | "auto" | "100%";
        [fields.label]: string;
        [fields.link]: LinkFieldValue;
        [fields.description]: string;
        [fields.alignment]: "flex-start" | "center" | "flex-end" | "none";
    };
}

export interface CallToActionStyles {
    cta?: ButtonPresentationProps;
    description?: TypographyProps;
    labelWrapper?: InjectableCss;
    label?: TypographyProps;
    labelIcon?: InjectableCss;
}

export const callToActionStyles: CallToActionStyles = {
    cta: {
        shape: "rectangle",
    },
    description: {
        color: "text",
        size: 20,
        css: css`
            display: block;
            text-align: center;
            ${breakpoint("sm", "up")} {
                text-align: left;
            }
            strong {
                color: ${getColor("primary")};
            }
        `,
    },
    label: {
        color: "primary",
        css: css`
            display: block;
            text-align: center;
            font-size: ${x => x.theme.design.typography.size.base};
            ${breakpoint("sm", "up")} {
                font-size: ${x => x.theme.design.typography.size.smaller};
                min-width: ${pxToRem(100)};
                max-width: ${pxToRem(100)};
                text-align: right;
            }
        `,
    },
    labelIcon: {
        css: css`
            border: ${pxToRem(2)} solid ${getColor("primary")};
            border-radius: 50%;
            margin-left: ${pxToRem(8)};
            width: ${pxToRem(28)};
            height: ${pxToRem(28)};
            svg {
                margin: auto;
                color: ${getColor("primary")};
                width: ${pxToRem(24)};
                height: ${pxToRem(24)};
            }
        `,
    },
};

const styles = callToActionStyles;

export const CallToAction: FC<OwnProps> = ({ fields }) => {
    const history = useHistory();
    const link = useGetLink(fields.link);
    const { isInShell } = useShellContext();

    const onClick = () => {
        if (!link.url || fields.alignment === "none") {
            return;
        }

        if (link.url.startsWith("http")) {
            if (isInShell) {
                return;
            }

            window.location.href = link.url;
        } else {
            history.push(link.url);
        }
    };

    const alignmentStyles = css`
        cursor: ${fields.alignment === "none" ? "default" : "pointer"};
        margin: ${pxToRem(64)} auto;
        padding: ${pxToRem(24)};
        background: ${getColor("common.disabledDark")};
        border: none;
        width: 100%;
        &:hover {
            background: ${fields.alignment === "none" ? getColor("common.disabledDark") : getColor("common.disabled")};
        }
        ${breakpoint("sm", "up")} {
            width: ${fields.size === "boxed" ? "auto" : fields.size};
            display: flex;
            align-items: ${fields.alignment === "none" ? "center" : fields.alignment};
        }
        ${breakpoint("md", "up")} {
            max-width: ${fields.size === "boxed" ? pxToRem(560) : "none"};
        }
    `;

    const labelWrapperStyles = css`
        display: ${fields.alignment === "none" ? "none" : "flex"};
        justify-content: center;
        align-items: center;
        margin-top: ${pxToRem(8)};
        svg {
            color: ${getColor("primary")};
            font-size: ${x => x.theme.design.typography.size.larger};
        }
        ${breakpoint("sm", "up")} {
            margin-top: 0;
        }
    `;

    const ctaContent = (
        <>
            <Typography {...styles.description}>{parse(fields.description, parserOptions)}</Typography>
            <StyledWrapper css={labelWrapperStyles}>
                <Typography {...styles.label}>{fields.label}</Typography>
                <StyledWrapper {...styles.labelIcon}>
                    <ArrowForward />
                </StyledWrapper>
            </StyledWrapper>
        </>
    );

    const ctaButton =
        fields.alignment === "none" ? (
            <StyledWrapper {...styles.cta} css={alignmentStyles}>
                {ctaContent}
            </StyledWrapper>
        ) : (
            <Button {...styles.cta} onClick={onClick} css={alignmentStyles}>
                {ctaContent}
            </Button>
        );

    return ctaButton;
};

const widgetModule: WidgetModule = {
    component: CallToAction,
    definition: {
        group: "Basic",
        icon: "rectangle-wide",
        fieldDefinitions: [
            {
                name: fields.size,
                displayName: "CTA Layout",
                editorTemplate: "RadioButtonsField",
                defaultValue: "boxed",
                fieldType: "General",
                sortOrder: 1,
                options: [
                    {
                        displayName: "Boxed",
                        value: "boxed",
                    },
                    {
                        displayName: "Auto Width",
                        value: "auto",
                    },
                    {
                        displayName: "Full Width",
                        value: "100%",
                    },
                ],
            },
            {
                name: fields.description,
                displayName: "CTA Description",
                editorTemplate: "RichTextField",
                defaultValue: "",
                fieldType: "Translatable",
                sortOrder: 1,
            },
            {
                name: fields.alignment,
                displayName: "CTA Link",
                editorTemplate: "RadioButtonsField",
                defaultValue: "center",
                fieldType: "General",
                sortOrder: 2,
                options: [
                    {
                        displayName: "Top",
                        value: "flex-start",
                    },
                    {
                        displayName: "Center",
                        value: "center",
                    },
                    {
                        displayName: "Bottom",
                        value: "flex-end",
                    },
                    {
                        displayName: "None",
                        value: "none",
                    },
                ],
            },
            {
                name: fields.label,
                displayName: "CTA Label",
                editorTemplate: "TextField",
                defaultValue: "Click to find out more",
                fieldType: "Translatable",
                sortOrder: 3,
                isVisible: widget => widget.fields.alignment !== "none",
            },
            {
                name: fields.link,
                displayName: "Button Link",
                editorTemplate: "LinkField",
                defaultValue: {
                    value: "",
                    type: "Page",
                },
                fieldType: "General",
                sortOrder: 4,
                isVisible: widget => widget.fields.alignment !== "none",
            },
        ],
    },
};

export default widgetModule;
